import {
    Component, Mixins, Watch,
} from 'vue-property-decorator';
import FormsMixin from '@groot-components/shared/mixins/forms/index';
import Classes from '@groot-components/shared/mixins/classes';
import config from '@groot-components/shared/config';
import { v4 as uuid } from 'uuid';
import { apiUrl } from '@groot-nodes/services/helpers';
import { digits } from '@groot-nodes/helpers/number';
import CoreMixin from '@groot-nodes/mixins/core';
import { get } from 'lodash/object';
import { YFieldWrapper } from '@groot-components/field-wrapper';
import vueDropzone from 'vue2-dropzone-vue3';
import Props from './props';

interface SingleFileUploaderRef extends Vue {
    classList: any;
}

interface SingleFileDropzoneRef extends Vue {
    removeFile: (e: any) => void;
    removeAllFiles:(e: any) => void;
}

/**
 * @name Uploader
 */
@Component({
    components: {
        vueDropzone,
        YFieldWrapper,
    },
})
class YUploader extends Mixins(FormsMixin, Props, Classes, CoreMixin) {
    digits = digits

    /**
     * Refs types
     */
    $refs!: {
        singleFileUploader: SingleFileUploaderRef;
        singleFileDropzone: SingleFileDropzoneRef;
    };
    
    /**
     * Props list that active by developer
     */
    activeCommonProps: Array<string> = ['color', 'size', 'sharp', 'raised', 'block'];

    /**
     * The css parent class of the component
     */
    parentClass: string = this.componentClass(this.baseClass, config.prefix.uploader);

    model = null;
    uploadedFiles = null;
    uploadingMessage: string = null;
    tempAttachments = [];

    /**
     * vue2-dropzone config
     */
    statics = {
        thumbnailWidth      : 768,
        thumbnailHeight     : null,
        timeout             : 6000000,
        addRemoveLinks      : false,
        clickable           : true,
        hiddenInputContainer: '.dropzone-wrapper',
        url                 : apiUrl('uploader-upload', 1),
        headers             : {
            Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null,
        },
    };

    /**
     * Icon preview
     */
    typesIcon = {
        image   : require('./assets/images/file-type/image.svg'), // eslint-disable-line global-require
        video   : require('./assets/images/file-type/video.svg'), // eslint-disable-line global-require
        document: require('./assets/images/file-type/document.svg'), // eslint-disable-line global-require
        file    : require('./assets/images/file-type/file.svg'), // eslint-disable-line global-require
    };

    /**
     * Message trans
     */
    trans = {
        errorCommon                 : this.$t('message.error.upload.common'),
        dictRemoveFile              : this.$t('components.uploader.dictRemoveFile'),
        dictRemoveFileConfirmation  : this.$t('components.uploader.dictRemoveFileConfirmation'),
        dictCancelUpload            : this.$t('components.uploader.dictCancelUpload'),
        dictUploadCanceled          : this.$t('components.uploader.dictUploadCanceled'),
        dictCancelUploadConfirmation: this.$t('components.uploader.dictCancelUploadConfirmation'),
        dictFileTooBig              : this.$t('components.uploader.dictFileTooBig'),
        dictInvalidFileType         : this.$t('components.uploader.dictInvalidFileType'),
        dictResponseError           : this.$t('components.uploader.dictResponseError'),
        dictMaxFilesExceeded        : this.$t('components.uploader.dictMaxFilesExceeded'),
    };

    /**
     * Mimes
     *
     * @see https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types
     */
    mimes = {
        jpg : 'image/jpeg',
        jpeg: 'image/jpeg',
        png : 'image/png',
        svg : 'image/svg+xml',
        gif : 'image/gif',
        ico : 'image/vnd.microsoft.icon',
        pdf : 'application/pdf',
        mp3 : 'audio/mpeg',
        mpga: 'audio/mpeg',
        mpeg: 'video/mpeg',
        mp4 : 'video/mp4',
        m4v : 'video/mp4',
        zip : 'application/zip',
        doc : 'application/msword',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        json: 'application/json',
    };

    /**
     * UUID
     */
    keyHelper: string = uuid();

    /**
     * Create Common Props for the uploader.
     * 
     * @returns {object} - List of props
     */
    get commonProps(): Record<string, any> {
        const commonProps: Record<string, any> = {
            baseClass: this.baseClass,
        };
        this.activeCommonProps.forEach((item) => {
            if (this.elements && this.elements[item]) {
                commonProps[item] = this.elements[item];
            } else {
                commonProps[item] = this[item];
            }
        });
        return commonProps;
    }

    /**
     * Create classes array for the uploader.
     * 
     * @returns {Array} - Array of classes
     */
    get classes(): Array<string> {
        const cls: Array<string> = [];
        cls.push(...this.commonClasses('Uploader', this.commonProps, this.baseClass || config.prefix.uploader));
        if (this.getValue('linear')) {
            cls.push('is-linear');
        }
        return cls;
    }

    /**
     * Return files resources
     * 
     * @returns {Array}
     */
    get getFiles(): Array<any> {
        return this.getValue('files');
    }

    /**
     * Generate dropzone options
     *
     * @returns {Object}
     */
    get dropzoneOptions(): {[k: string]: any} {
        const options: {[k: string]: any} = {
            headers: {},
        };

        options.maxFilesize = this.getValue('maxFileSize') * 1;
        options.maxFiles = this.getValue('maxFiles') * 1;
        options.acceptedFiles = this.acceptedFilesParsed.join(',');
        options.headers = this.getValue('headers', {});

        return {
            ...options,
            ...this.statics,
            ...this.trans,
            ...this.getValue('options', {}),
        };
    }

    /**
     * Check if reach upload limit
     *
     * @returns {boolean}
     */
    get reachUploadLimit(): boolean {
        return (this.maxFilesParsed === this.model.length);
    }

    /**
     * Check if uploader is single
     *
     * @returns {boolean}
     */
    get isSingle(): boolean {
        return this.maxFilesParsed === 1;
    }

    /**
     * Check type of uploader by maxFiles
     *
     * @returns {string}
     */
    get uploaderType(): string {
        return this.maxFilesParsed === 1 ? 'single' : 'multiple';
    }

    /**
     * Convert max files to number
     * 
     * @returns {number}
     */
    get maxFilesParsed(): number {
        return this.getValue('maxFiles') * 1;
    }

    /**
     * Accepted files parsed
     *
     * @returns {Array}
     */
    get acceptedFilesParsed(): Array<any> {
        const arr = this.getValue('acceptedFiles');
        const mimes = new Set();
        arr.forEach((ext: PropertyKey) => {
            if (this.mimes.hasOwnProperty(ext)) {
                mimes.add(this.mimes[ext]);
            }
        });
        return Array.from(mimes);
    }

    /**
     * Change string value to array
     *
     * @param {*} value
     * @returns {string[]}
     */
    @Watch('value')
    onChangeValue(value: any): string[] { // eslint-disable-line consistent-return
        if (typeof value === 'string') {
            return [value];
        }
    }

    /**
     * Change string value to array
     *
     * @param {*} value
     */
    @Watch('model')
    onChangeModel(value: any) {
        this.$emit('input', value);
    }

    /**
     * @inheritDoc
     */
    created() {
        this.model = [];
        this.uploadedFiles = [];

        if (this.value && typeof this.value === 'object' && !Array.isArray(this.value)) {
            this.makeModel([this.value]);
        } else if (Array.isArray(this.value) && this.value.length) {
            this.makeModel(this.value);
        } else if (this.getFiles) {
            if (Array.isArray(this.getFiles) && this.getFiles.length) {
                this.makeModel(this.getFiles);
            } else if (typeof this.getFiles === 'object' && !Array.isArray(this.getFiles)) {
                this.makeModel([this.getFiles]);
            }
        }
    }

    /**
     * Make model
     *
     * @param {Array} files
     */
    makeModel(files: any[]) {
        files.forEach((item) => {
            if (item) {
                this.model.push(item.id);

                const {
                    id, link, mime_type, versions, // eslint-disable-line camelcase
                } = item;
                const thumb = versions && versions.panel_thumb ? versions.panel_thumb : null;
                this.uploadedFiles.push({
                    id,
                    link,
                    mime_type,
                    thumb,
                });
            }
        });
    }

    /**
     * Handle file error
     *
     * @param {object} file
     * @param {object} error
     */
    handleUploadError(file, error) {
        this.uploadingMessage = 'error';

        let msg = this.trans.errorCommon;

        // Define Message
        if (typeof error === 'string') {
            msg = error;
            if (msg.includes('{maxFiles}')) {
                msg = msg.replace('{maxFiles}', digits(this.getValue('maxFiles'), this.$i18n.locale));
            } else if (msg.includes('{filesize}')) {
                msg = msg.replace('{filesize}', digits((file.size / 1024 / 1024).toFixed(2), this.$i18n.locale));
                msg = msg.replace('{maxFileSize}', digits(this.getValue('maxFileSize'), this.$i18n.locale));
            }
            this.$toast.error(msg);
        } else if (error.message) {
            msg = error.message;
            this.$toast.error(msg);
        } else {
            this.handleError({ response: { data: error } });
        }

        setTimeout(() => {
            // eslint-disable-next-line array-callback-return
            this.tempAttachments.map((attachment) => {
                if (attachment.id === file.upload.uuid) {
                    /* eslint-disable no-param-reassign */
                    attachment.progress = `${attachment.progress ? Math.floor(attachment.progress) : 0}`;
                    attachment.msg = msg;
                /* eslint-enable no-param-reassign */
                }
            });
        }, 300);
    }

    /**
     * Successful Upload
     *
     * @param {*} file
     * @param {*} response
     */
    successUpload(file, response) {
        const {
            id, link, mime_type, versions, // eslint-disable-line camelcase
        } = response ? response.metadata : get(JSON.parse(file.xhr.response), 'metadata', {});

        const { name, size } = file;
        if (this.reachUploadLimit) {
            return;
        }

        this.$emit('success', {
            id,
            link,
            mime_type,
            versions,
            name,
            size,
        });

        const thumb = versions && versions.panel_thumb ? versions.panel_thumb : null;

        this.model.push(id);

        this.deleteIndex(this.tempAttachments, file.upload.uuid);

        this.uploadedFiles.push({
            id,
            link,
            mime_type,
            thumb,
            name,
            size,
        });
    }

    /**
     * Handle some actions when a file added
     *
     * @param {*} file
     */
    fileAdded(file) {
        const attachment: any = {};
        attachment.id = file.upload.uuid;
        attachment.name = file.name;
        attachment.mime_type = file.type;
        attachment.extension = `.${file.type.split('/')[1]}`;
        attachment.isLoading = true;
        attachment.progress = null;
        attachment.size = file.size;
        setTimeout(() => {
            attachment.thumb = file.dataURL;
            attachment.link = file.dataURL;
            this.tempAttachments.push(attachment);
        }, 300);
      
        this.uploadingMessage = 'adding';
        if (this.maxFilesParsed === 1 && this.$refs.hasOwnProperty('singleFileUploader')) {
            this.$refs.singleFileUploader.classList.add('afterUpload');
        }
    }

    /**
     * function where we get the upload progress
     *
     * @param {*} file 
     * @param {*} progress 
     * @param {*} bytesSent 
     */
    uploadProgress(file, progress, bytesSent) {
        // eslint-disable-next-line array-callback-return
        this.tempAttachments.map((attachment) => {
            if (attachment.name === file.name) {
                /* eslint-disable no-param-reassign */
                attachment.progress = `${Math.floor(progress)}`;
                /* eslint-enable no-param-reassign */
            }
        });
    }

    /**
     * Handle some actions when a file removed
     */
    removedFile() {
        if (this.maxFilesParsed === 1 && this.$refs.hasOwnProperty('singleFileUploader') && this.$refs.singleFileUploader) {
            this.$refs.singleFileUploader.classList.remove('afterUpload');
        }
    }

    /**
     * Sending Action
     *
     * @param {*} file
     * @param {*} xhr
     * @param {*} formData
     */
    sending(file, xhr, formData) {
        if (this.getValue('payload')) {
            formData.append('payload', this.getValue('payload'));
        }
    }

    /**
     * Remove file
     *
     * @param {*} id
     * @param {boolean} RemoveAllFiles
     */
    removeFile(id: any, RemoveAllFiles: false) {
        this.deleteIndex(this.tempAttachments, id);
        this.deleteIndex(this.uploadedFiles, id);
        this.deleteIndex(this.model, id);

        if (RemoveAllFiles) {
            this.$refs.singleFileDropzone.removeAllFiles(true);
            this.tempAttachments = [];
        } else {
            this.$refs.singleFileDropzone.removeFile(id);
        }

        this.keyHelper = uuid();
        this.$emit('remove', id);
        this.uploadingMessage = null;
    }

    /**
     * Handle Remove file item
     *
     * @param {*} array
     * @param {*} id
     */
    deleteIndex(array: any, id: any) {
        const index = array.findIndex((file: any) => {
            if (typeof file === 'object') {
                return file.id === id;
            }
            return file === id;
        });
        if (index !== -1) {
            array.splice(index, 1);
        }
    }

    /**
     * Get proper icon for each file
     *
     * @param {*} file
     * @returns {string}
     */
    uploadedFileIcon(file) {
        const mime = file.mime_type;

        if (!mime) {
            return this.typesIcon.file;
        }

        if (mime.includes('image')) {
            return this.typesIcon.image;
        }

        if (mime.includes('video')) {
            return this.typesIcon.video;
        }

        if (mime.includes('pdf')) {
            return this.typesIcon.document;
        }

        return this.typesIcon.file;
    }

    /**
     * Check file is image
     *
     * @param {*} file
     * @returns {boolean}
     */
    isImage(file) {
        return file && file.mime_type && !!file.mime_type.includes('image');
    }

    /**
     * Get file thumbnail
     *
     * @param {*} file
     * @param {boolean} useThumb
     * @returns {*}
     */
    imageThumb(file, useThumb = false) {
        return useThumb ? (file.thumb || file.link) : file.link;
    }

    /**
     * Check if object is empty
     *
     * @param {object} obj
     * @returns {boolean}
     */
    isEmpty(obj: string | any[]): boolean | number {
        return (obj && Array.isArray(obj) && obj.length)
                    || (typeof obj === 'object' && !Array.isArray(obj));
    }

    /**
     * Emit completion of queue
     */
    queueComplete() {
        if (!this.getValue('simple') && !this.getValue('isSingle')) {
            // TODO Should develop multiple mode
            // this.$refs.modal.close();
        }
        this.$emit('complete');
    }

    /**
     * Just do nothing
     */
    doNothing() {}
}

export default YUploader;
